import { type RequestResponse } from '@atlassian/twg-common';

import { getAnalyticsClient } from './analytics-client';
import { EVENT_CHANNEL } from './constants';

/**
 * One of the five HTTP-timing event keys generated by codegen for
 * Each key has its own attribute payload (see analytics.spec.yaml).
 */
type TimingEventKey =
	| 'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics'
	| 'operational.orgMetricsRequest.completed.twgMicrositeOrgMetrics'
	| 'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors'
	| 'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts'
	| 'operational.collaborationUsersRequest.completed.twgMicrositeCollaborationUsers'
	| 'operational.userMetricsRequest.completed.twgMicrositeUserMetrics';

const ACTION_BY_KEY: Record<TimingEventKey, { actionSubject: string; actionSubjectId: string }> = {
	'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics': {
		actionSubject: 'globalMetricsRequest',
		actionSubjectId: 'twgMicrositeGlobalMetrics',
	},
	'operational.orgMetricsRequest.completed.twgMicrositeOrgMetrics': {
		actionSubject: 'orgMetricsRequest',
		actionSubjectId: 'twgMicrositeOrgMetrics',
	},
	'operational.appConnectorsRequest.completed.twgMicrositeAppConnectors': {
		actionSubject: 'appConnectorsRequest',
		actionSubjectId: 'twgMicrositeAppConnectors',
	},
	'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts': {
		actionSubject: 'availableProductsRequest',
		actionSubjectId: 'twgMicrositeAvailableProducts',
	},
	'operational.userMetricsRequest.completed.twgMicrositeUserMetrics': {
		actionSubject: 'userMetricsRequest',
		actionSubjectId: 'twgMicrositeUserMetrics',
	},
	'operational.collaborationUsersRequest.completed.twgMicrositeCollaborationUsers': {
		actionSubject: 'collaborationUsersRequest',
		actionSubjectId: 'twgMicrositeCollaborationUsers',
	},
};

/**
 * Wraps a `RequestResponse`-returning promise (the standard shape returned
 * by `twg-common`'s `request`/`requestWithAbort`) with timing analytics.
 *
 * Resolves with the original RequestResponse so callers consume it as
 * before; the timing event fires as a side-effect on every resolution
 * (success OR failure). Aborted requests still fire (statusCode = null).
 *
 * Caller-side helper, not invasive to the util files.
 *
 * @example
 *   const res = await withRequestTiming(
 *     'operational.globalMetricsRequest.completed.twgMicrositeGlobalMetrics',
 *     fetchGlobalMetrics(),
 *   );
 */
export const withRequestTiming = async <T extends Record<string, unknown> | unknown[]>(
	eventKey: TimingEventKey,
	promise: Promise<RequestResponse<T>>,
	extraAttrs?: Record<string, unknown>,
): Promise<RequestResponse<T>> => {
	const start = Date.now();
	let response: RequestResponse<T>;
	try {
		response = await promise;
	} catch (e) {
		fireTimingEvent(eventKey, {
			durationMs: Date.now() - start,
			statusCode: null,
			success: false,
			...extraAttrs,
		});
		throw e;
	}
	fireTimingEvent(eventKey, {
		durationMs: Date.now() - start,
		statusCode: response.statusCode ?? null,
		success: response.success,
		...extraAttrs,
	});
	return response;
};

/**
 * Adapter for non-RequestResponse promises (e.g. Relay `fetchQuery` or any
 * arbitrary async work). Fires the timing event on resolve/reject; the
 * caller decides how to derive `success`/`statusCode` (defaults to
 * `success: true` on resolve and `statusCode: null` on reject).
 */
export const withTimingAdapter = async <T>(
	eventKey: TimingEventKey,
	promise: Promise<T>,
	deriveAttrs?: (
		result: { ok: true; value: T } | { ok: false; error: unknown },
	) => Record<string, unknown>,
): Promise<T> => {
	const start = Date.now();
	try {
		const value = await promise;
		fireTimingEvent(eventKey, {
			durationMs: Date.now() - start,
			statusCode: null,
			success: true,
			...(deriveAttrs?.({ ok: true, value }) ?? {}),
		});
		return value;
	} catch (e) {
		fireTimingEvent(eventKey, {
			durationMs: Date.now() - start,
			statusCode: null,
			success: false,
			...(deriveAttrs?.({ ok: false, error: e }) ?? {}),
		});
		throw e;
	}
};

/**
 * Fires a track event imperatively via the singleton AnalyticsWebClient.
 * Safe to call from non-React contexts (Jotai atoms, util modules).
 */
const fireTimingEvent = (eventKey: TimingEventKey, attributes: Record<string, unknown>): void => {
	let client;
	try {
		client = getAnalyticsClient();
	} catch {
		// Analytics not initialised yet (e.g. during SSR or pre-bootstrap);
		// silently drop the event.
		return;
	}
	const { actionSubject, actionSubjectId } = ACTION_BY_KEY[eventKey];
	void client.sendOperationalEvent({
		action: 'completed',
		actionSubject,
		actionSubjectId,
		source: 'twgMicrositeHome',
		attributes,
		tags: [EVENT_CHANNEL],
	});
};
